import Container from '@brands/Components/Container/Container';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { UserRoleName } from '../../../../services/identity/types/UserProfile';
import styles from '../../adminDashboard.module.scss';
import AddAdminUserPage from '../AddUserPages/Admin/AddAdminUserPage';
import AddCareAdvisorUserPage from '../AddUserPages/CareAdvisor/AddCareAdvisorUserPage';
import AddCollectorUserPage from '../AddUserPages/Collector/AddCollectorUserPage';
import AddOrganizationAdminUserPage from '../AddUserPages/OrganizationAdmin/AddOrganizationAdminUserPage';
import AddPatientUserPage from '../AddUserPages/Patient/AddPatientUserPage';
import AddProviderUserPage from '../AddUserPages/Provider/AddProviderUserPage';

const validCreateRoles = new Set<string>([
  UserRoleName.Patient,
  UserRoleName.Provider,
  UserRoleName.CareAdvisor,
  UserRoleName.Collector,
  UserRoleName.Admin,
  UserRoleName.SuperAdmin,
  UserRoleName.OrganizationAdmin,
]);

export type AdminAddUserContentProps = {
  /** Same as the `role` search param on `/users/new` — `null` means Patient (default). */
  createRoleFromQuery: string | null;
  onClose: () => void;
};

/** Form only — parent supplies admin shell (`Container` + layout) when embedded in `/admin/dashboard`. */
export const AdminAddUserContent: React.FC<AdminAddUserContentProps> = ({ createRoleFromQuery, onClose }) => {
  const resolvedRole = createRoleFromQuery || UserRoleName.Patient;
  const isValidRole = validCreateRoles.has(resolvedRole);

  let content: JSX.Element;
  if (!isValidRole) {
    content = (
      <div>
        <h2>Invalid role</h2>
        <p>Provided role is not supported for user creation.</p>
      </div>
    );
  } else
    switch (resolvedRole) {
      case UserRoleName.Provider:
        content = <AddProviderUserPage onClose={onClose} />;
        break;
      case UserRoleName.CareAdvisor:
        content = <AddCareAdvisorUserPage onClose={onClose} />;
        break;
      case UserRoleName.Collector:
        content = <AddCollectorUserPage onClose={onClose} />;
        break;
      case UserRoleName.Admin:
      case UserRoleName.SuperAdmin:
        content = <AddAdminUserPage role={resolvedRole} onClose={onClose} />;
        break;
      case UserRoleName.OrganizationAdmin:
        content = <AddOrganizationAdminUserPage role={UserRoleName.OrganizationAdmin} onClose={onClose} />;
        break;
      case UserRoleName.Patient:
        content = <AddPatientUserPage onClose={onClose} />;
        break;
      default:
        content = (
          <div>
            <h2>Invalid role</h2>
            <p>Provided role is not supported for user creation.</p>
          </div>
        );
        break;
    }

  return <React.Fragment key={resolvedRole}>{content}</React.Fragment>;
};

const AddUserPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const createRole = new URLSearchParams(location.search).get('role');

  const closePage = (): void => {
    navigate('/admin/dashboard#users');
  };

  return (
    <Container
      patientDashboard
      isVisibleOnMobile={false}
      childClassName={styles.adminDashboardShell}
      containerStyle={{ overflowY: 'hidden' }}
      childrenStyle={{
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <div className={styles.adminLayout}>
        <main className={styles.adminContent}>
          <AdminAddUserContent createRoleFromQuery={createRole} onClose={closePage} />
        </main>
      </div>
    </Container>
  );
};

export default AddUserPage;
